import { useState } from "react";
import { joinWaitlist } from "@/lib/api";
import { formatDateLong } from "@/lib/format";
import { toast } from "sonner";
import { X, Bell, ArrowRight } from "lucide-react";

export default function WaitlistModal({ open, date, time, onClose }) {
  const [form, setForm] = useState({ school_name: "", teacher_name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await joinWaitlist({ date, time, ...form });
      setDone(true);
      toast.success("Added to the waiting list");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not join waiting list";
      toast.error(typeof msg === "string" ? msg : "Could not join waiting list");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setDone(false);
    setForm({ school_name: "", teacher_name: "", email: "", phone: "" });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center px-4"
      onClick={close}
      data-testid="waitlist-modal"
    >
      <div
        className="bg-white border-2 border-slate-900 brutal-shadow-lg w-full max-w-lg animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-slate-900 bg-sky-500 text-white">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5" strokeWidth={2.5} />
            <div>
              <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] opacity-90">
                Waiting list
              </div>
              <div className="font-display uppercase text-lg leading-none">Notify me if free</div>
            </div>
          </div>
          <button
            onClick={close}
            className="p-1 hover:bg-white/10"
            data-testid="waitlist-close"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-6">
          {done ? (
            <div data-testid="waitlist-confirmed">
              <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-2">
                You&apos;re on the list
              </div>
              <h3 className="font-display text-2xl uppercase mb-3">Got it!</h3>
              <p className="text-sm text-slate-700 leading-relaxed mb-6">
                We&apos;ll email <span className="font-bold">{form.email}</span> the moment this slot frees up
                on <span className="font-mono-tight font-bold">{formatDateLong(date)}</span> at{" "}
                <span className="font-mono-tight font-bold">{time}</span>. Bookings are first-come, first-served,
                so watch your inbox.
              </p>
              <button
                onClick={close}
                className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-5 py-2.5"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="waitlist-form">
              <p className="text-sm text-slate-700 leading-relaxed mb-4">
                This slot is booked, but we&apos;ll email you if it becomes available.
              </p>
              <div className="mb-4 font-mono-tight text-sm flex flex-wrap gap-2 items-center">
                <span className="bg-slate-900 text-white px-3 py-1">{formatDateLong(date)}</span>
                <span className="bg-sky-500 text-white px-3 py-1">{time}</span>
              </div>

              <div className="grid gap-4">
                <WLField label="School name">
                  <input
                    required
                    value={form.school_name}
                    onChange={change("school_name")}
                    className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                    data-testid="waitlist-school-name"
                  />
                </WLField>
                <WLField label="Teacher name">
                  <input
                    required
                    value={form.teacher_name}
                    onChange={change("teacher_name")}
                    className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                    data-testid="waitlist-teacher-name"
                  />
                </WLField>
                <div className="grid grid-cols-2 gap-4">
                  <WLField label="Email">
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={change("email")}
                      className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                      data-testid="waitlist-email"
                    />
                  </WLField>
                  <WLField label="Phone">
                    <input
                      required
                      value={form.phone}
                      onChange={change("phone")}
                      className="w-full bg-white border-2 border-slate-900 p-2.5 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                      data-testid="waitlist-phone"
                    />
                  </WLField>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-5 py-2.5 flex items-center gap-2 disabled:opacity-60"
                  data-testid="waitlist-submit"
                >
                  {submitting ? "Adding…" : "Join waiting list"}
                  {!submitting && <ArrowRight className="w-4 h-4" strokeWidth={3} />}
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="bg-white text-slate-900 font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2.5"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const WLField = ({ label, children }) => (
  <label className="block">
    <div className="font-mono-tight text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">{label}</div>
    {children}
  </label>
);
